import { QuestionmarkTooltip } from "@dewo/app/components/QuestionmarkTooltip";
import { OrganizationOptionsInput } from "@dewo/app/graphql/types";
import { Checkbox, Divider, Form, Skeleton, Tag, Typography } from "antd";
import _ from "lodash";
import React, { FC, useCallback, useMemo } from "react";
import { useOrganizationDetails, useUpdateOrganization } from "../hooks";

type FormValues = OrganizationOptionsInput;

interface Props {
  organizationId: string;
}

const Content: FC<Props> = ({ organizationId }) => {
  const { organization } = useOrganizationDetails(organizationId);
  const updateOrganization = useUpdateOrganization();
  const handleChange = useCallback(
    async (changed: Partial<FormValues>) => {
      updateOrganization({ id: organizationId, options: changed });
    },
    [organizationId, updateOrganization]
  );

  const initialValues = useMemo(
    (): FormValues => _.omit(organization?.options, "__typename"),
    [organization]
  );

  if (!organization) {
    return <Skeleton />;
  }

  return (
    <Form<FormValues>
      layout="vertical"
      requiredMark={false}
      initialValues={initialValues}
      onValuesChange={handleChange}
    >
      <Form.Item name="roadmap" valuePropName="checked">
        <Checkbox>
          <QuestionmarkTooltip
            marginLeft={8}
            title="View all ongoing projects on a timeline. The roadmap will be visible in the sidebar menu."
          >
            Enable Project Roadmap
            <Tag color="green" style={{ marginLeft: 8 }}>
              New
            </Tag>
          </QuestionmarkTooltip>
        </Checkbox>
      </Form.Item>
      <Form.Item name="roles" valuePropName="checked">
        <Checkbox>
          <QuestionmarkTooltip
            marginLeft={8}
            title="Feature specific roles and let contributors apply to them. The role center will be visible in the sidebar menu."
          >
            Enable Roles Center
          </QuestionmarkTooltip>
        </Checkbox>
      </Form.Item>
      <Form.Item name="mintTaskNFTs" valuePropName="checked">
        <Checkbox>
          <QuestionmarkTooltip
            marginLeft={8}
            title="As a V1 for on-chain credentialing, we mint ERC-721 NFTs with the task metadata of the work you complete on Dework. It will show up on the task cards as well as on OpenSea, see eg: https://opensea.io/collection/dework"
          >
            Mint Task NFTs
          </QuestionmarkTooltip>
        </Checkbox>
      </Form.Item>
    </Form>
  );
};

export const OrganizationGeneralSettings: FC<Props> = ({ organizationId }) => (
  <>
    <Typography.Title level={4} style={{ marginBottom: 4 }}>
      General
    </Typography.Title>
    <Divider style={{ marginTop: 0 }} />
    <Content organizationId={organizationId} />
  </>
);
